// ============================================================
// utils/tokenStorage.ts
// Gestión segura de tokens en localStorage
// ── Access token:  vida corta (5–15 min, según backend)
// ── Refresh token: vida larga, se rota en cada uso
// ============================================================

const KEYS = {
  ACCESS_TOKEN:      'auth_access_token',
  REFRESH_TOKEN:     'auth_refresh_token',
  ACCESS_ISSUED:     'auth_access_issued_at',
  ACCESS_EXPIRES:    'auth_access_expires_ms',
  USER:              'auth_user',
  PASSWORD_EXPIRED:  'auth_password_expired',
} as const;

export const tokenStorage = {

  // ── Access Token ──────────────────────────────────────────

  saveAccessToken(token: string, expiresInMs: number): void {
    localStorage.setItem(KEYS.ACCESS_TOKEN,   token);
    localStorage.setItem(KEYS.ACCESS_ISSUED,  Date.now().toString());
    localStorage.setItem(KEYS.ACCESS_EXPIRES, expiresInMs.toString());
  },

  getAccessToken(): string | null {
    return localStorage.getItem(KEYS.ACCESS_TOKEN);
  },

  /**
   * Retorna true si el access token expira en menos de `bufferMs`
   * (por defecto 30 seg). Así se refresca antes de que caduque.
   */
  isAccessTokenExpiringSoon(bufferMs = 30_000): boolean {
    const issued  = parseInt(localStorage.getItem(KEYS.ACCESS_ISSUED)  ?? '0', 10);
    const expires = parseInt(localStorage.getItem(KEYS.ACCESS_EXPIRES) ?? '0', 10);
    if (!issued || !expires) return true;
    return Date.now() >= issued + expires - bufferMs;
  },

  isAccessTokenExpired(): boolean {
    return this.isAccessTokenExpiringSoon(0);
  },

  // ── Refresh Token ─────────────────────────────────────────

  saveRefreshToken(token: string): void {
    localStorage.setItem(KEYS.REFRESH_TOKEN, token);
  },

  getRefreshToken(): string | null {
    return localStorage.getItem(KEYS.REFRESH_TOKEN);
  },

  // ── Usuario serializado ───────────────────────────────────

  saveUser(user: object): void {
    localStorage.setItem(KEYS.USER, JSON.stringify(user));
  },

  getUser<T>(): T | null {
    const raw = localStorage.getItem(KEYS.USER);
    if (!raw) return null;
    try { return JSON.parse(raw) as T; } catch { return null; }
  },

  // ── Contraseña expirada ───────────────────────────────────

  savePasswordExpired(expired: boolean): void {
    if (expired) {
      localStorage.setItem(KEYS.PASSWORD_EXPIRED, '1');
    } else {
      localStorage.removeItem(KEYS.PASSWORD_EXPIRED);
    }
  },

  isPasswordExpired(): boolean {
    return localStorage.getItem(KEYS.PASSWORD_EXPIRED) === '1';
  },

  // ── Limpieza total (logout) ───────────────────────────────

  clearAll(): void {
    Object.values(KEYS).forEach(k => localStorage.removeItem(k));
  },
};
